import React from 'react'
import styled from '@emotion/styled'
import ReactDatePicker from 'react-datepicker'
import { enGB, enUS } from 'date-fns/locale'
import moment from 'moment'
import { iconSet } from 'view/components/icons'
import { ValidationIssue } from 'api'
import { Field } from './Field'
import { Focusable, StyledInput } from './BaseInput'
import { DateLike, KeyCodes, toLocalDateString } from 'utils'
import { ValidationTooltip } from '../tooltip'
import 'react-datepicker/dist/react-datepicker.css'
import './DatePicker.css'
import { createInvalidValueValidationIssue, ValidationType } from 'model'
import { Locale, Translation } from 'locale'
import { isNil } from 'lodash-es'

export interface DatePickerProps {
  id?: string
  className?: string
  value: DateLike | null
  dateFormat?: string
  showMonthPicker?: boolean
  placeholder?: string
  filterDate?: (value: Date) => boolean
  minDate?: Date
  maxDate?: Date
  validationIssues?: ValidationIssue[]
  readOnly?: boolean
  disabled?: boolean
  dataTestId?: string
  onUpdate: (value: string | null) => unknown
  onPaste?: (evt: React.ClipboardEvent) => unknown
  dispatchNullWhenInvalid?: boolean
  validateDate?: boolean
  withPortal?: boolean
}

export interface DatePickerState {
  validButFilteredDate: string | null
}

const DatePickerWrapper = styled.div`
  position: relative;
`

const CalendarIcon = styled.div`
  width: 16px;
  height: 16px;
  position: absolute;
  top: 4px;
  right: 8px;
  pointer-events: none;
`

// workaround for https://github.com/Hacker0x01/react-datepicker/issues/1471
// and https://github.com/Hacker0x01/react-datepicker/issues/730
class WorkaroundDatePicker extends ReactDatePicker {
  deferFocusInput = () => {
    ;(this as any).cancelFocusInput()
  }
}

const ReactDatePickerStyled = styled(StyledInput.withComponent(WorkaroundDatePicker))`
  cursor: pointer;
  padding-right: 30px;
`

const DatePickerInput = React.forwardRef((props: any, ref: React.Ref<any> | null) => {
  const { validationIssues, dataTestId, validButFilteredDate, value, ...rest } = props
  return (
    <ValidationTooltip validationIssues={validationIssues || []}>
      <input
        data-test-id={dataTestId}
        ref={ref}
        {...rest}
        value={validButFilteredDate !== null ? validButFilteredDate : value}
        onFocusCapture={evt => evt.currentTarget.select()}
      />
    </ValidationTooltip>
  )
})

export class DatePicker extends React.Component<DatePickerProps, DatePickerState> implements Focusable {
  state: DatePickerState = {
    validButFilteredDate: null
  }

  render() {
    const {
      id,
      className,
      dateFormat,
      showMonthPicker,
      placeholder,
      filterDate,
      minDate,
      maxDate,
      readOnly,
      disabled,
      validationIssues = [],
      dataTestId,
      onPaste,
      value,
      validateDate,
      dispatchNullWhenInvalid
    } = this.props

    const portalId = this.props.withPortal ? 'root' : undefined

    const { validButFilteredDate } = this.state

    // Filters out missing mandatory validation issues when validating date locally and dispatching null on valid but filtered dates
    const localValidationIssues = validateDate
      ? validationIssues
          .filter(
            validationIssue =>
              !dispatchNullWhenInvalid || !validButFilteredDate || validationIssue.type !== ValidationType.MISSING_MANDATORY
          )
          .concat(
            this.getDateValidationIssues(
              !isNil(validButFilteredDate) ? moment(validButFilteredDate!, dateFormat?.toUpperCase() ?? 'DD/MM/YYYY').toDate() : value
            )
          )
      : validationIssues

    return (
      <DatePickerWrapper className={className} onKeyDown={this.handleKeyDown}>
        <ReactDatePickerStyled
          ref={this.datepicker}
          customInput={
            <DatePickerInput
              dataTestId={dataTestId}
              validButFilteredDate={validButFilteredDate}
              validationIssues={localValidationIssues}
              onPaste={onPaste}
            />
          }
          id={id}
          selected={value ? moment(value).toDate() : null}
          locale={dateFormat === 'MM/dd/yyyy' ? (enUS as any) : (enGB as any)}
          dateFormat={dateFormat ?? 'dd/MM/yyyy'}
          showMonthYearPicker={showMonthPicker}
          placeholderText={placeholder}
          filterDate={filterDate}
          minDate={minDate}
          portalId={portalId}
          maxDate={maxDate}
          readOnly={readOnly}
          disabled={disabled}
          validationIssues={localValidationIssues}
          disabledKeyboardNavigation={false}
          showDisabledMonthNavigation={true}
          onChangeRaw={this.handleTextChange}
          onChange={this.handleChange}
        />
        <CalendarIcon>{iconSet.calendarIcon}</CalendarIcon>
      </DatePickerWrapper>
    )
  }

  private handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    switch (evt.keyCode) {
      case KeyCodes.UP:
      case KeyCodes.DOWN:
      case KeyCodes.ENTER:
        evt.stopPropagation()
        break
      default:
        break
    }
  }

  private datepicker = React.createRef<WorkaroundDatePicker>()

  private handleTextChange = (event: React.FocusEvent<HTMLInputElement>) => {
    const { dateFormat, onUpdate, dispatchNullWhenInvalid } = this.props
    const value = event.target.value

    if (!value) {
      return
    }

    const valueDate = moment(value, dateFormat?.toUpperCase() ?? 'DD/MM/YYYY')

    // If this is a filtered date, then update state and onUpdate null value (handleSelect does not get called)
    if (this.getDateValidationIssues(valueDate.toDate()).length > 0) {
      this.setState({
        validButFilteredDate: value
      })
      onUpdate(dispatchNullWhenInvalid || !valueDate.isValid() ? null : valueDate.format('YYYY-MM-DD'))
    }
  }

  private handleChange = (date: Date | null) => {
    const { onUpdate, dispatchNullWhenInvalid } = this.props
    this.setFocus() // TODO: reinvestigate: https://weareadaptive.atlassian.net/browse/BQ-3612

    if (this.getDateValidationIssues(date).length > 0) {
      onUpdate(dispatchNullWhenInvalid || !date ? null : toLocalDateString(date))
      this.forceUpdate()
      return
    }

    this.setState({
      validButFilteredDate: null
    })

    onUpdate(date ? toLocalDateString(date) : null)
  }

  public setFocus() {
    if (this.datepicker.current) {
      this.datepicker.current.setFocus()
    }
  }

  private getDateValidationIssues = (dateValue: string | Date | null): ValidationIssue[] => {
    const { minDate, maxDate, filterDate } = this.props
    if (!dateValue) {
      return []
    }

    const validationIssues: ValidationIssue[] = []

    const momentDate = moment(dateValue)

    if (!momentDate.isValid()) {
      validationIssues.push(createInvalidValueValidationIssue('date', Locale.translate(Translation.SC_UNRECOGNISED_DATE_FORMAT)))
    }

    if (filterDate && !filterDate(momentDate.toDate())) {
      validationIssues.push(createInvalidValueValidationIssue('date', Locale.translate(Translation.SC_DATE_NOT_ALLOWED)))
    }

    if (minDate && momentDate.isBefore(minDate, 'days')) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          'date',
          Locale.translate(Translation.SC_DATE_MUST_BE_AFTER, {
            date: minDate.toDateString()
          })
        )
      )
    }

    if (maxDate && momentDate.isAfter(maxDate, 'days')) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          'date',
          Locale.translate(Translation.SC_DATE_MUST_BE_BEFORE, {
            date: maxDate.toDateString()
          })
        )
      )
    }

    return validationIssues
  }
}

export const DatePickerField = Field(DatePicker)
